import { useState, useEffect, useCallback } from "react";
import { detectDuplicates } from "../lib/detectDuplicates";
import type { TimeEntry, DuplicatePair } from "../lib/detectDuplicates";

interface UseTeamworkDuplicatesResult {
  pairs: DuplicatePair[];
  loading: boolean;
  error: string | null;
  deleting: Set<string>;
  deleteEntry: (id: string) => Promise<void>;
  refresh: () => void;
}

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

export function useTeamworkDuplicates(): UseTeamworkDuplicatesResult {
  const [pairs, setPairs] = useState<DuplicatePair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    fetch(
      `/api/teamwork/time-entries?fromdate=${formatDate(weekAgo)}&todate=${formatDate(today)}`,
    )
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<{ "time-entries": TimeEntry[] }>;
      })
      .then((data) => {
        const entries = data["time-entries"] ?? [];
        setPairs(detectDuplicates(entries));
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const deleteEntry = useCallback(async (id: string) => {
    setDeleting((prev) => new Set(prev).add(id));
    try {
      const res = await fetch(`/api/teamwork/time-entries/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPairs((prev) => prev.filter((p) => p.a.id !== id && p.b.id !== id));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Delete failed: ${msg}`);
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  return { pairs, loading, error, deleting, deleteEntry, refresh };
}
